import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import Highcharts from 'highcharts';
import * as api from '../../api';
import { FacilitiesService } from '../../core/facilities/facilities.service';
import * as Colors from '../colors';
import { AbstractSummaryComponent } from './abstract-summary.component';

/**
 * Base class for Pharmacy Cost Summary widgets and drilldown pages.
 */
@Component({
    template: '',
    host: {
        'class' : 'abstract-summed-aggregate-summary'
      }    
  })
export abstract class AbstractSummedAggregateSummaryComponent extends AbstractSummaryComponent implements AfterViewInit, OnDestroy {

    private currencyPipe: CurrencyPipe = new CurrencyPipe("en");


    /** Construct */
    constructor(facilitiesSvc: FacilitiesService) {
        super(facilitiesSvc);
    }

    /**
     * Transform SummedAggregateData from API into a more useful structure for drawing column and bar charts.
     */
    protected transformSummedAggregateData(apiData: api.SummedAggregateData): TransformedSummedAggregate {
        let intermediate = new TransformedSummedAggregate(apiData);
        if (apiData.SubGroups) {
            for (let outterGroupIdx = 0; outterGroupIdx < apiData.SubGroups.length; ++outterGroupIdx) {
                let outterGroup = apiData.SubGroups[outterGroupIdx];
                intermediate.outterGroupNames.push(outterGroup.GroupName);
                if (!outterGroup.SubGroups)
                    continue;

                for (let innerGroup of outterGroup.SubGroups) {
                    if (innerGroup.Sum === 0)
                        continue;

                    let points = intermediate.innerCountPerOutterGroupIndex.get(innerGroup.GroupName);
                    if (!points) {
                        // First time seeing this inner group
                        intermediate.innerGroupNames.push(innerGroup.GroupName);
                        points = Array(11).fill(0);
                        intermediate.innerCountPerOutterGroupIndex.set(innerGroup.GroupName, points);
                    }

                    points[outterGroupIdx] = innerGroup.Sum;
                }
            }
        }

        return intermediate;
    }

    /**
     * Populate a column chart with aggregate data.
     */
    protected populateSummedAggregateColumns(chart: Highcharts.Chart, aggregateData: TransformedSummedAggregate): void {
        this.populateSummedAggregateBars(chart, aggregateData);
    }

    /**
     * Populate a stacked bar chart with aggregate data.
     */
    protected populateSummedAggregateBars(chart: Highcharts.Chart, aggregateData: TransformedSummedAggregate): void {
        chart.xAxis[0].setCategories(aggregateData.outterGroupNames);
        let numDataPoints = aggregateData.outterGroupNames.length;
        if (numDataPoints > 0) {
            for (let innerName of aggregateData.innerGroupNames) {
                chart.addSeries({
                    type: 'bar',
                    name: innerName,
                    data: aggregateData.innerCountPerOutterGroupIndex.get(innerName).slice(0, numDataPoints)
                }, false);
            }
        }
        chart.hideLoading();
        let haveData = aggregateData.outterGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a simple single-series bar chart.
     */
    protected populateSummedSimpleBars(chart: Highcharts.Chart, data: api.SummedAggregateData): void {
        let aggregateData = data.SubGroups;
        let names = [];

        if (aggregateData) {
            let series = chart.addSeries({ type: 'bar', name: ' ', showInLegend: false }, false);
            for (let outterGroup of aggregateData) {
                if (outterGroup.Sum) {
                    names.push(outterGroup.GroupName);
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: outterGroup.Sum,
                        color: Colors.ORANGE
                    }, false);
                }
            }
            chart.xAxis[0].setCategories(names, true);
        }

        chart.hideLoading();
        this.setSubtitle(chart, names.length ? '' : 'No Data', names.length === 0);
        this.redrawChart(chart);
    }

    /**
     * Populate a pie chart with sum & aggregate data.
     */
    protected populateSummedPie(
        chart: Highcharts.Chart,
        data: api.SummedAggregateData,
    ): void {
        let aggregateData = data.SubGroups;

        if (aggregateData) {
            let series = chart.addSeries({type: 'pie', name: ' '});
            let colors: string[];
            switch (aggregateData.length) {
                case 1:
                    colors = Colors.DONUT1_CHART_COLORS;
                    break;
                case 2:
                    colors = Colors.DONUT2_CHART_COLORS;
                    break;
                case 3:
                    colors = Colors.DONUT3_CHART_COLORS;
                    break;
                default:
                    colors = Colors.DONUT_CHART_COLORS;
                    break;
            }

            let colorIdx = 0;
            for (let outterGroup of aggregateData) {
                if (outterGroup.Sum) {
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: outterGroup.Sum,
                        color: colors[colorIdx]
                    }, true);
                }
                ++colorIdx;
            }

        }

        chart.hideLoading();
        this.setSubtitle(chart,
                         aggregateData ? this.formatCurrency0(Math.round(data.Sum)) : "No Data",
                         !aggregateData || data.Sum === 0);
        this.redrawChart(chart);
    }


    /**
     * Format a dollar value, without cents.
     */
    protected formatCurrency0(value: number): string {
        return this.currencyPipe.transform(Math.round(value), "USD", 'symbol', ".0");
    }
}

export class TransformedSummedAggregate {
    outterGroupNames: string[] = [];
    innerGroupNames: string[] = [];
    innerCountPerOutterGroupIndex = new Map<string,number[]>();

    constructor(public apiData: api.SummedAggregateData) {
    }
}
